import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHome, FiUser, FiAward, FiMail, FiFileText, FiShield, FiMap, FiExternalLink, FiMessageSquare } = FiIcons;

const Sitemap = () => {
  const pages = [
    {
      title: 'Home',
      path: '/',
      icon: FiHome,
      description: 'Main landing page with services overview and contact information'
    },
    {
      title: 'About Us',
      path: '/about-us',
      icon: FiUser,
      description: 'Learn about the team behind Jet\'n\'Seal'
    },
    {
      title: 'Why Us',
      path: '/why-us',
      icon: FiAward,
      description: 'Discover what makes our cleaning services exceptional'
    },
    {
      title: 'Contact Us',
      path: '/contact-us',
      icon: FiMail,
      description: 'Get in touch for quotes and service enquiries'
    },
    {
      title: 'Terms & Conditions',
      path: '/terms',
      icon: FiFileText,
      description: 'Our service terms and conditions'
    },
    {
      title: 'Privacy Policy',
      path: '/privacy',
      icon: FiShield,
      description: 'How we protect and use your personal information'
    },
    {
      title: 'Sitemap',
      path: '/sitemap',
      icon: FiMap,
      description: 'Complete navigation guide to our website'
    }
  ];

  const services = [
    'Patio Cleaning',
    'Driveway Cleaning', 
    'Pressure Washing',
    'Surface Cleaning',
    'Sealing Services',
    'Outdoor Cleaning'
  ];

  const serviceAreas = [
    'Malton',
    'Norton',
    'Pickering',
    'Rillington',
    'Thornton-le-Dale',
    'Kirkbymoorside'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">Sitemap</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Navigate through all pages and sections of the Jet'n'Seal website easily.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Sitemap Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main Pages */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Website Pages</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pages.map((page, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                >
                  <Link to={page.path} className="block">
                    <div className="flex items-center space-x-3 mb-3">
                      <SafeIcon icon={page.icon} className="w-6 h-6 text-blue-600" />
                      <h3 className="text-lg font-semibold text-gray-900">{page.title}</h3>
                      <SafeIcon icon={FiExternalLink} className="w-4 h-4 text-gray-400" />
                    </div>
                    <p className="text-gray-600 text-sm">{page.description}</p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Services Section */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white p-8 rounded-xl shadow-lg"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Services</h3>
              <div className="space-y-3">
                {services.map((service, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex items-center space-x-3 p-3 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    <span className="text-gray-700 font-medium">{service}</span>
                  </motion.div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-gray-200">
                <Link
                  to="/contact-us"
                  className="inline-flex items-center text-blue-600 font-medium hover:text-blue-700 transition-colors"
                >
                  Get Quote for Services
                  <SafeIcon icon={FiExternalLink} className="w-4 h-4 ml-2" />
                </Link>
              </div>
            </motion.div>

            {/* Service Areas Section */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white p-8 rounded-xl shadow-lg"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Areas We Serve</h3>
              <div className="space-y-3">
                {serviceAreas.map((area, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex items-center space-x-3 p-3 rounded-lg hover:bg-green-50 transition-colors"
                  >
                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    <span className="text-gray-700 font-medium">{area}</span>
                  </motion.div>
                ))}
                <div className="pt-3">
                  <div className="flex items-center space-x-3 p-3 rounded-lg bg-gray-50">
                    <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                    <span className="text-gray-600 font-medium">& Surrounding Areas</span>
                  </div>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-gray-200">
                <Link
                  to="/contact-us"
                  className="inline-flex items-center text-blue-600 font-medium hover:text-blue-700 transition-colors"
                >
                  Check Service Availability
                  <SafeIcon icon={FiExternalLink} className="w-4 h-4 ml-2" />
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mt-12 bg-blue-50 p-8 rounded-xl text-center"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Quick Contact</h3>
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-8">
              <Link
                to="/contact-us"
                className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                <SafeIcon icon={FiMessageSquare} className="w-5 h-5" />
                <span>Contact Us</span>
              </Link>
            </div>
          </motion.div>

          {/* XML Sitemap Note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mt-8 text-center"
          >
            <p className="text-gray-600 text-sm">
              Looking for the XML sitemap for search engines? 
              <a href="/sitemap.xml" className="text-blue-600 hover:text-blue-700 font-medium ml-1">
                View XML Sitemap
              </a>
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Sitemap;